/**
 * Top-level views of a rule — the SAME four for every flavour. A chat-authored
 * rule answers its recommendation settings in the Atlas conversation and a
 * template rule on its create panel, but once the rule exists both edit them
 * on the Display settings tab: a merchant cannot tell from the URL which
 * flavour a rule is and must find the settings in one place.
 *
 * Versions is top-level rather than a view inside Advanced. A rule's publish
 * history is a rule-level fact — what is live, what was live, what failed —
 * and burying it behind `advanced_agentic_logic_enabled` meant the merchants
 * who most need to answer "what are shoppers seeing, and how do I go back"
 * were the ones who never learned versions exist. It is also where the only
 * publish affordance leads now that Save no longer deploys a serving rule
 * (`publishOnSave.ts`).
 */
export const RULE_DETAIL_TABS = [
  { key: "selection_logic", label: "Selection logic" },
  { key: "display_settings", label: "Display settings" },
  { key: "versions", label: "Versions" },
  { key: "advanced", label: "Advanced" },
] as const;

export type RuleDetailTabKey = (typeof RULE_DETAIL_TABS)[number]["key"];

/**
 * Opening a rule lands on the readable Selection logic graph. Explicit tab
 * links still override this default, so expert links can open Advanced.
 */
export const DEFAULT_RULE_DETAIL_TAB: RuleDetailTabKey = "selection_logic";

/**
 * A template rule opens on the canvas instead. Its Advanced tab is raw config
 * JSON — deliberately the expert view — so the readable graph is the right
 * landing surface, and the rule is already complete by the time it exists.
 */
export const DEFAULT_TEMPLATE_RULE_DETAIL_TAB: RuleDetailTabKey =
  "selection_logic";

/**
 * The selected tab, carried in the URL so a link can land on one — Atlas sends
 * the author to the Selection logic canvas after it builds a rule — and so a
 * reload keeps the reader where they were.
 */
export const RULE_DETAIL_TAB_PARAM = "tab";

/**
 * The tabs an org actually sees. The Advanced tab is the expert view — raw
 * rule code or configuration and the publish gates — and is withheld unless
 * the org's `advanced_agentic_logic_enabled` toggle is on. It is the ONLY
 * gated tab: Versions used to ride inside it and no longer does.
 */
export const visibleRuleDetailTabs = (advancedEnabled: boolean) =>
  advancedEnabled
    ? RULE_DETAIL_TABS
    : RULE_DETAIL_TABS.filter((tab) => tab.key !== "advanced");

/** Whether a URL value names a tab of the rule page. */
export const isRuleDetailTabKey = (
  value: string | null | undefined
): value is RuleDetailTabKey =>
  RULE_DETAIL_TABS.some((tab) => tab.key === value);
